import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CalendarPlus, Lock, Sun } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { startNewDay, clearError } from '../../redux/slices/shiftSlice';

export default function StartNewDayModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { starting } = useSelector((state) => state.shift);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';
  if (!isAdmin) return null;

  const handleConfirm = async () => {
    const result = await dispatch(startNewDay());
    if (startNewDay.fulfilled.match(result)) {
      toast.success(`New business day started for ${result.payload.businessDay?.businessDate || 'today'}`);
      dispatch(clearError());
      onClose();
    } else {
      toast.error(result.payload || 'Failed to start a new day');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!starting) onClose(); }}
      title="Start a New Business Day?"
      description="Open the shop for a fresh business day. Sales are currently blocked because the last day was ended."
      size="md"
      closeOnBackdrop={!starting}
      showCloseButton={!starting}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <CalendarPlus size={18} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Open for a new day</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
              A new business day will be opened and <span className="font-medium">{user?.name}</span> will be
              recorded as the opener. Sales and checkout are re-enabled immediately.
            </p>
          </div>
        </div>

        <p className="flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
          <Sun size={13} className="mt-0.5 flex-shrink-0" />
          <span>
            A business day is not tied to the calendar midnight &ndash; it covers the full session until the
            next <span className="font-medium text-gray-900 dark:text-white">SALE END</span>.
          </span>
        </p>

        <p className="flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
          <Lock size={13} className="mt-0.5 flex-shrink-0" />
          <span>
            Opening will be recorded as <span className="font-medium text-gray-900 dark:text-white">{user?.name}</span>.
          </span>
        </p>
      </div>

      <div className="flex gap-3 w-full mt-5">
        <Button variant="secondary" onClick={onClose} disabled={starting} className="flex-1">
          Cancel
        </Button>
        <Button variant="primary" loading={starting} icon={CalendarPlus} onClick={handleConfirm} className="flex-1">
          Start New Day
        </Button>
      </div>
    </Modal>
  );
}